import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import type { User } from '../types';
import { userAPI } from '../utils/apiClient';
import logger from '../utils/logger';

import { useAuth } from './AuthContext';

/**
 * User data edits type - tracks pending edits to user records
 */
interface UserDataEdits {
  [userId: string]: {
    username?: string;
    password?: string;
    rights?: string;
  };
}

/**
 * User add data type
 */
interface AddUserData {
  name?: string;
  username?: string;
  rights?: string;
  password?: string;
}

/**
 * UserContext value type
 */
interface UserContextValue {
  users: User[];
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  userDataEdits: UserDataEdits;
  onEditUserName: (user: User, value: string) => void;
  onEditUserPassword: (user: User, value: string) => void;
  onEditUserRights: (user: User, value: string) => void;
  onSubmitUserDataEdits: () => Promise<void>;
  onCancelUserDataEdits: () => void;
  onDeleteUser: (id: string) => Promise<void>;
  onAddUser: (data: AddUserData) => Promise<void>;
  userCount: number;
}

/**
 * UserContext - Manages user management state
 *
 * Provides user data and related functionality for admin users.
 * Handles fetching, editing, adding, and deleting users.
 */
const UserContext = createContext<UserContextValue | null>(null);

/**
 * Props for UserProvider component
 */
interface UserProviderProps {
  children: ReactNode;
}

/**
 * Hook to access user context
 * @returns User context value
 * @throws Error if used outside UserProvider
 */
export const useUsers = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUsers must be used within a UserProvider');
  }
  return context;
};

/**
 * UserProvider Component - Provides user management state to children
 *
 * Manages user data fetching and state. Only fetches data for admin users.
 * Provides functionality for editing, adding, and deleting users.
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [userDataEdits, setUserDataEdits] = useState<UserDataEdits>({});

  /**
   * Fetch users data from API
   */
  const fetchUsers = async (): Promise<void> => {
    if (!user || !isAdmin) {
      setUsers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use userAPI (apiClient with Bearer header) instead of raw fetch to avoid
      // "No authentication token" errors when localStorage isn't available or
      // hasn't been written yet by AuthContext.
      const data = await userAPI.getUsers();

      // Handle both old API format (direct array) and new format (with success field)
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (
        (data as { success: boolean; users: User[] }).success &&
        Array.isArray((data as { success: boolean; users: User[] }).users)
      ) {
        setUsers((data as { success: boolean; users: User[] }).users);
      } else {
        throw new Error('Invalid users data format');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Error fetching users:', errorMessage);
      setError(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle editing user data fields
   */
  const onEditUserDataField = (targetUser: User, field: string, value: string): void => {
    const newEdits: UserDataEdits = JSON.parse(JSON.stringify(userDataEdits));
    let userData: UserDataEdits[string] = {};
    if (Object.prototype.hasOwnProperty.call(newEdits, targetUser.id)) {
      userData = newEdits[targetUser.id];
    }
    (userData as Record<string, string>)[field] = value;
    newEdits[targetUser.id] = userData;
    setUserDataEdits(newEdits);
  };

  const onEditUserName = (targetUser: User, value: string): void => {
    logger.debug('Edit Username:', targetUser, value);
    onEditUserDataField(targetUser, 'username', value);
  };

  const onEditUserPassword = (targetUser: User, value: string): void => {
    logger.debug('Edit User password:', targetUser, value);
    onEditUserDataField(targetUser, 'password', value);
  };

  const onEditUserRights = (targetUser: User, value: string): void => {
    logger.debug('Edit User rights:', targetUser, value);
    onEditUserDataField(targetUser, 'rights', value);
  };

  /**
   * Submit user data edits
   */
  const onSubmitUserDataEdits = async (): Promise<void> => {
    logger.debug('Submit user edits:', userDataEdits);

    try {
      for (const userId of Object.keys(userDataEdits)) {
        const edits = userDataEdits[userId];
        if (Object.keys(edits).length > 0) {
          await userAPI.updateUser(userId, edits);
          logger.info(`User ${userId} updated successfully`);
        }
      }
      setUserDataEdits({});
      await fetchUsers(); // Refresh user list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Error updating user:', errorMessage);
      setError(errorMessage);
    }
  };

  /**
   * Cancel user data edits
   */
  const onCancelUserDataEdits = (): void => {
    setUserDataEdits({});
  };

  /**
   * Delete a user
   */
  const onDeleteUser = async (id: string): Promise<void> => {
    logger.debug('Delete user:', id);

    try {
      await userAPI.deleteUser(id);
      logger.info(`User ${id} deleted successfully`);
      setUsers(users.filter((u) => u.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Error deleting user:', errorMessage);
      setError(errorMessage);
    }
  };

  /**
   * Add a new user
   */
  const onAddUser = async (data: AddUserData): Promise<void> => {
    logger.debug('Add user:', data);
    const username = data.name || data.username || '';

    if (username.length < 2) {
      logger.error('Username too short');
      setError('Username must be at least 2 characters');
      return;
    }

    try {
      await userAPI.createUser({
        username,
        rights: data.rights || 'filter',
        password: data.password,
      });
      logger.info(`User ${username} created successfully`);
      await fetchUsers(); // Refresh user list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Error creating user:', errorMessage);
      setError(errorMessage);
    }
  };

  /**
   * Fetch users data when user changes or becomes admin
   */
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  /**
   * Periodically refresh users data (every 30 seconds)
   */
  useEffect(() => {
    if (!user || !isAdmin) {
      return;
    }
    const interval = setInterval(fetchUsers, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const value: UserContextValue = {
    users,
    loading,
    error,
    fetchUsers,
    userDataEdits,
    onEditUserName,
    onEditUserPassword,
    onEditUserRights,
    onSubmitUserDataEdits,
    onCancelUserDataEdits,
    onDeleteUser,
    onAddUser,
    userCount: users.length,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
